import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import Tooltip from '@ublend-npm/react-tooltip';
import Separator from '@ublend-npm/aulaui-next-web/Separator';
import { isInstructor } from '../../../../../utils/user';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
} from '../../../common/Dialog';
import StudentsNotInGroup from './StudentsNotInGroup';
import FieldSet, {
  TextField,
  Checkbox,
  FieldRow,
  AvatarContainer,
} from '../../../common/FieldSets';

import AvatarUploader from '../../../common/AvatarUploader/AvatarUploader';
import ParticipantsFieldSet from './ParticipantsFieldSet.container';
import Initial from './initial';

export const lastFieldSetStyle = {
  paddingBottom: 0,
};

const SCREEN = {
  main: 'mainScreen',
  studentsNotInGroup: 'studentsNotInGroupScreen',
};

const GroupInner = ({
  mode,
  initial,
  studentsWithNoGroup: initialStudentsWithNoGroup,
  saving,
  isSetLocked,
  onSave,
  onClose,
  deleting,
  onDelete,
  uploadGroupAvatar,
}) => {
  const [name, setName] = useState(initial.name);
  const [participants, setParticipants] = useState(initial.participants);
  const [avatar, setAvatar] = useState(initial.avatar);
  const [hasConversation, setHasConversation] = useState(
    initial.hasConversation
  );
  const [
    conversationIncludesEducators,
    setConversationIncludesEducators,
  ] = useState(initial.conversationIncludesEducators);
  const [usersAdded, setUsersAdded] = useState({});
  const [studentsWithNoGroup, setStudentsWithNoGroup] = useState(
    initialStudentsWithNoGroup
  );
  const resetState = () => {
    setName(initial.name);
    setParticipants(initial.participants);
    setAvatar(initial.avatar);
    setHasConversation(initial.hasConversation);
    setConversationIncludesEducators(initial.conversationIncludesEducators);
    setUsersAdded({});
    setStudentsWithNoGroup(initialStudentsWithNoGroup);
  };
  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    const changedName = initial.name !== name;
    const changedParticipants =
      initial.participants.length !== participants.length;
    const changedAvatar = initial.avatar !== avatar;
    const changedHasConversation = initial.hasConversation !== hasConversation;
    const changedConversationIncludesEducators =
      initial.conversationIncludesEducators !== conversationIncludesEducators;
    const hasAdded = Object.keys(usersAdded).length !== 0;
    setHasChanges(
      changedName ||
        changedParticipants ||
        changedAvatar ||
        changedHasConversation ||
        changedConversationIncludesEducators ||
        hasAdded
    );
  }, [
    name,
    participants,
    avatar,
    hasConversation,
    conversationIncludesEducators,
  ]);

  const [dialogScreen, setDialogScreen] = useState(SCREEN.main);

  const handleNameChange = useCallback((event) => setName(event.target.value), [
    name,
  ]);

  const handleSelectParticipant = useCallback(
    (user) => {
      setUsersAdded({ ...usersAdded, [user.id]: true });
      setParticipants([user, ...participants]);
    },
    [participants, usersAdded]
  );

  const handleRemoveParticipant = useCallback(
    (userId) => {
      const updateStudentsAdded = { ...usersAdded };
      delete updateStudentsAdded[userId];
      setUsersAdded(updateStudentsAdded);
      setParticipants(participants.filter(({ id }) => userId !== id));

      const previouslyAddedUser = initial.participants.find(
        ({ id }) => userId === id
      );
      if (previouslyAddedUser && !isInstructor(previouslyAddedUser.id)) {
        setStudentsWithNoGroup([previouslyAddedUser, ...studentsWithNoGroup]);
      }
    },
    [participants, usersAdded]
  );

  const handleRemoveStudentNotInGroup = (userId) => {
    const updateStudentsAdded = { ...usersAdded };
    delete updateStudentsAdded[userId];
    setUsersAdded(updateStudentsAdded);
    setParticipants(participants.filter(({ id }) => userId !== id));
  };

  const handleAddStudentNotInGroup = (userId) => {
    const addedUser = studentsWithNoGroup.find(({ id }) => id === userId);
    setUsersAdded({ ...usersAdded, [userId]: true });
    setParticipants([addedUser, ...participants]);
  };

  const handleConversationIncludesEducatorsChange = useCallback(
    () => setConversationIncludesEducators(!conversationIncludesEducators),
    [conversationIncludesEducators]
  );

  const handleGroupConversationChange = useCallback(() => {
    if (hasConversation) {
      setHasConversation(false);
      setConversationIncludesEducators(false);
    } else {
      setHasConversation(true);
    }
  }, [hasConversation, conversationIncludesEducators]);

  const groupConversation = useMemo(
    () => (
      <FieldSet title="Group messages" style={lastFieldSetStyle}>
        <FieldRow>
          <Checkbox
            label="Create group conversation"
            checked={hasConversation}
            onChange={handleGroupConversationChange}
          />
        </FieldRow>
        <FieldRow>
          <Checkbox
            label="Include educators in group conversation"
            checked={conversationIncludesEducators}
            disabled={!hasConversation}
            onChange={handleConversationIncludesEducatorsChange}
          />
        </FieldRow>
      </FieldSet>
    ),
    [hasConversation, conversationIncludesEducators]
  );
  const canSave = useMemo(() => name.length > 0 && participants.length > 0, [
    name,
    participants,
  ]);

  const handleSave = useCallback(() => {
    onSave({
      name,
      avatar,
      participants,
      hasConversation,
      conversationIncludesEducators,
    });
  }, [
    name,
    avatar,
    participants,
    hasConversation,
    conversationIncludesEducators,
  ]);

  const [isAvatarUploading, setAvatarUploading] = useState(false);
  const handlerAvatarClick = useCallback(
    async (file) => {
      setAvatarUploading(true);
      const fileUrl = await uploadGroupAvatar(file);
      setAvatarUploading(false);
      if (fileUrl) {
        setAvatar(fileUrl);
      }
    },
    [avatar]
  );

  const title = useMemo(
    () => (mode === 'create' ? 'New group' : 'Edit group'),
    [mode]
  );

  const deleteAction = useMemo(
    () =>
      mode === 'edit' && !isSetLocked
        ? {
            className: 'delete-group-button',
            label: 'Delete group',
            onClick: onDelete,
            disabled: saving,
            loading: deleting,
            confirmation: {
              warningLabel: 'Are you sure you would like to delete this group?',
              acceptLabel: 'Yes',
              cancelLabel: 'No',
            },
          }
        : undefined,
    [mode, saving, deleting, isSetLocked]
  );

  const dialogActions = (
    <DialogActions
      primary={{
        className: 'save-group-button',
        label: 'Save',
        disabled: !(hasChanges && canSave),
        onClick: handleSave,
        loading: saving,
      }}
      secondary={{
        className: 'discard-group-button',
        label: 'Discard changes',
        disabled: !hasChanges,
        onClick: resetState,
        confirmation: {
          type: 'info',
          warningLabel: 'Do you confirm you want to discard all changes?',
          acceptLabel: 'Discard',
          cancelLabel: 'Keep',
        },
      }}
      tertiary={deleteAction}
    />
  );

  const mainDialogContent = (
    <>
      <DialogTitle id={`${mode}-group-dialogtitle`} onClose={onClose}>
        {title}
      </DialogTitle>
      <DialogContent>
        <FieldSet title="Group info">
          <FieldRow>
            <AvatarContainer>
              <Tooltip title="Upload group image" position="left">
                <AvatarUploader
                  onUpload={handlerAvatarClick}
                  isLoading={isAvatarUploading}
                  size="large"
                  url={avatar}
                  uploadEnable
                />
              </Tooltip>
            </AvatarContainer>
            <TextField
              id="group-name"
              label="Name"
              value={name}
              onChange={handleNameChange}
            />
          </FieldRow>
        </FieldSet>
        <Separator />
        <ParticipantsFieldSet
          selected={participants}
          studentsWithNoGroup={studentsWithNoGroup}
          onSelect={handleSelectParticipant}
          onRemove={handleRemoveParticipant}
          isReadOnly={isSetLocked}
          onStudentsNotInGroupClick={() =>
            setDialogScreen(SCREEN.studentsNotInGroup)
          }
          usersAdded={usersAdded}
        />
        <Separator />
        {groupConversation}
      </DialogContent>
    </>
  );

  const studentsNotInGroupContent = (
    <StudentsNotInGroup
      groupName={name}
      studentsWithNoGroup={studentsWithNoGroup}
      usersAdded={usersAdded}
      onBack={() => setDialogScreen(SCREEN.main)}
      onSelect={handleAddStudentNotInGroup}
      onRemove={handleRemoveStudentNotInGroup}
      onClose={onClose}
    />
  );

  return (
    <React.Fragment>
      {dialogScreen === SCREEN.main
        ? mainDialogContent
        : studentsNotInGroupContent}
      {dialogActions}
    </React.Fragment>
  );
};

GroupInner.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  initial: Initial.propTypes,
  studentsWithNoGroup: PropTypes.arrayOf(PropTypes.shape({})),
  saving: PropTypes.bool.isRequired,
  isSetLocked: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  uploadGroupAvatar: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
  onDelete: PropTypes.func,
};

GroupInner.defaultProps = {
  initial: Initial.defaultProps,
  deleting: false,
  studentsWithNoGroup: [],
  onDelete: () => {},
};

export default GroupInner;
